import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  obtenerAcciones,
  obtenerAlcances,
  obtenerAtributos,
  obtenerOperadores,
  obtenerReglaPorId,
  obtenerTodasLasReglas,
  obtenerTiposRegla
} from '../../services/ReglasService';
import "./VistaReglas.css";

const normalizeDecision = (decision) => {
  const d = String(decision ?? '').trim().toUpperCase();
  if (d === 'AUTORIZA' || d === 'NO_AUTORIZA' || d === 'DERIVAR' || d === 'NO_REQUIERE_AUTORIZACION') return d;
  if (d === 'DERIVA' || d === 'DERIVADO') return 'DERIVAR';
  if (d === 'NO REQUIERE AUTORIZACION' || d === 'NO-REQUIERE-AUTORIZACION') return 'NO_REQUIERE_AUTORIZACION';
  return d || 'DERIVAR';
};

const hoyISO = () => new Date().toISOString().split('T')[0];

const extractItems = (raw) => {
  if (Array.isArray(raw)) return raw;
  return raw?.data || raw?.reglas || raw?.items || [];
};

const extractTotal = (raw, items) => {
  if (Array.isArray(raw)) return raw.length;
  return raw?.total ?? raw?.totalItems ?? raw?.totalRegistros ?? items.length;
};

const calcularEstadoVigencia = (regla) => {
  const hoy = hoyISO();
  const inicio = String(regla?.vigenteDesde || '').slice(0, 10);
  const fin = String(regla?.vigenteHasta || '').slice(0, 10);
  if (fin && fin < hoy) return { key: 'VENCIDA', label: 'Vencida' };
  if (inicio && inicio > hoy) return { key: 'FUTURA', label: 'Futura' };
  if (!regla?.vigente) return { key: 'DESHABILITADA', label: 'Deshabilitada' };
  return { key: 'VIGENTE', label: 'Vigente' };
};

// ===== Icono de sort =====
const SortIcon = ({ column, sortColumn, sortDir }) => {
  if (sortColumn !== column) {
    return <span className="sort-icon sort-icon-inactive">↕</span>;
  }
  return <span className="sort-icon sort-icon-active">{sortDir === 'asc' ? '↑' : '↓'}</span>;
};

const ACCIONES_FILTER = [
  { key: 'AUTORIZA',                  label: 'Autoriza',        cls: 'badge-autoriza' },
  { key: 'NO_AUTORIZA',               label: 'No autoriza',     cls: 'badge-no_autoriza' },
  { key: 'DERIVAR',                   label: 'Derivar',         cls: 'badge-derivar' },
  { key: 'NO_REQUIERE_AUTORIZACION',  label: 'No requiere',     cls: 'badge-no_requiere_autorizacion' },
];

const VIGENCIA_FILTER = [
  { key: 'VIGENTE',       label: 'Vigente',      cls: 'badge-vigencia-vigente' },
  { key: 'FUTURA',        label: 'Futura',        cls: 'badge-vigencia-futura' },
  { key: 'VENCIDA',       label: 'Vencida',       cls: 'badge-vigencia-vencida' },
  { key: 'DESHABILITADA', label: 'Deshabilitada', cls: 'badge-vigencia-deshabilitada' },
];

export const DEFAULT_RULES_SORT = {
  column: 'version',
  direction: 'desc',
};

const VistaReglas = () => {
  const [reglas, setReglas] = useState([]);
  const [totalReglas, setTotalReglas] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingPage, setLoadingPage] = useState(false);
  const [error, setError] = useState(null);
  const catalogosRef = useRef(null);
  const abortControllerRef = useRef(null);
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [paginaActual, setPaginaActual] = useState(1);
  const itemsPorPagina = 15;

  // Sort
  const [sortColumn, setSortColumn] = useState(DEFAULT_RULES_SORT.column);
  const [sortDir, setSortDir] = useState(DEFAULT_RULES_SORT.direction);

  // Filtros rápidos
  const [filterAccion, setFilterAccion] = useState('');
  const [filterVigencia, setFilterVigencia] = useState('');

  // Debounce buscador
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPaginaActual(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset página al cambiar filtros o sort
  useEffect(() => { setPaginaActual(1); }, [filterAccion, filterVigencia, sortColumn, sortDir]);

  useEffect(() => {
    cargarInicial();
    return () => { abortControllerRef.current?.abort(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!catalogosRef.current) return;
    cargarPagina(paginaActual, debouncedSearch, filterAccion, filterVigencia, sortColumn, sortDir);
  }, [paginaActual, debouncedSearch, filterAccion, filterVigencia, sortColumn, sortDir]); // eslint-disable-line react-hooks/exhaustive-deps

  const cargarInicial = async () => {
    setLoading(true);
    setError(null);
    try {
      const [
        accionesRaw, atributosRaw, operadoresRaw, tiposRaw, alcancesRaw, reglasRaw
      ] = await Promise.all([
        obtenerAcciones(), obtenerAtributos(), obtenerOperadores(),
        obtenerTiposRegla(), obtenerAlcances(),
        obtenerTodasLasReglas({
          page: 1,
          pageSize: itemsPorPagina,
          sortBy: DEFAULT_RULES_SORT.column,
          sortDir: DEFAULT_RULES_SORT.direction,
        })
      ]);

      const acciones  = Object.fromEntries((accionesRaw  || []).map((a) => [a.idAccion,   a]));
      const atributos = Object.fromEntries((atributosRaw || []).map((a) => [a.idAtributo, a]));
      const operadores= Object.fromEntries((operadoresRaw|| []).map((o) => [o.idOperador, o]));
      const tipos     = Object.fromEntries((tiposRaw     || []).map((t) => [t.idTipoRegla,t]));
      const alcances  = Object.fromEntries((alcancesRaw  || []).map((a) => [a.idAlcance,  a]));

      const maps = { acciones, atributos, operadores, tipos, alcances };
      catalogosRef.current = maps;

      const items = extractItems(reglasRaw);
      const total = extractTotal(reglasRaw, items);
      setTotalReglas(total);
      setReglas(items.map((r) => normalizarRegla(r, maps)));
    } catch (err) {
      setError('Error al cargar las reglas: ' + (err.message || 'Error desconocido'));
    } finally {
      setLoading(false);
    }
  };

  const cargarPagina = async (page, search, accion, vigencia, sortCol, sortDirection) => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    setLoadingPage(true);
    setError(null);
    try {
      const raw = await obtenerTodasLasReglas({
        page, pageSize: itemsPorPagina, search,
        accion: accion || '', vigencia: vigencia || '',
        sortBy: sortCol || '', sortDir: sortDirection || 'asc',
        signal
      });
      const items = extractItems(raw);
      const total = extractTotal(raw, items);
      setTotalReglas(total);
      setReglas(items.map((r) => normalizarRegla(r, catalogosRef.current)));
    } catch (err) {
      if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError' || err?.name === 'AbortError') return;
      setError('Error al cargar las reglas: ' + (err.message || 'Error desconocido'));
    } finally {
      setLoadingPage(false);
    }
  };

  const normalizarRegla = (regla, maps) => {
    const accion   = maps.acciones[regla.idAccion];
    const tipo     = maps.tipos[regla.idTipoRegla];
    const alcance  = maps.alcances[regla.idAlcance];
    const condiciones = Array.isArray(regla.condiciones)
      ? regla.condiciones.map((cond) => ({
          atributo: maps.atributos[cond.idAtributo]?.nombre || String(cond.idAtributo ?? ''),
          operador: maps.operadores[cond.idOperador]?.nombre || String(cond.idOperador ?? ''),
          valor:    cond.valorComparacion,
          tipoDato: null
        }))
      : [];

    return {
      ruleId:         regla.idRegla,
      idReglaVersion: regla.idReglaVersion,
      version:        regla.version,
      nombre:         regla.nombre || regla.descripcion || regla.idRegla,
      descripcion:    regla.descripcion || regla.nombre || '',
      plan:           regla.idPlan,
      prioridad:      regla.prioridad,
      vigente:        regla.activo,
      vigenteDesde:   regla.fechaInicioVigencia,
      vigenteHasta:   regla.fechaFinVigencia,
      updatedAt:      regla.updatedAt || regla.fechaActualizacion || null,
      prerequisito:   condiciones.some((c) => String(c.atributo).toLowerCase() === 'prerequisitos'),
      condiciones,
      accion: { decision: accion?.codigo || '', motivo: accion?.descripcion || '' },
      tipoRegla: tipo?.codigo   || '',
      alcance:   alcance?.nombre || ''
    };
  };

  const formatearFecha = (fechaStr) => {
    if (!fechaStr) return '';
    const iso = String(fechaStr).slice(0, 10);
    const [anio, mes, dia] = iso.split('-');
    if (anio && mes && dia) return `${dia}/${mes}/${anio}`;
    const fecha = new Date(fechaStr);
    return Number.isNaN(fecha.getTime()) ? '' : fecha.toLocaleDateString('es-AR');
  };

  // Sort: servidor maneja nombre/prioridad/version/vigencia/accion; condiciones es local
  const sortedFiltered = useMemo(() => {
    if (sortColumn !== 'condiciones') return reglas;
    return [...reglas].sort((a, b) => {
      const va = a.condiciones?.length ?? 0;
      const vb = b.condiciones?.length ?? 0;
      return sortDir === 'asc' ? va - vb : vb - va;
    });
  }, [reglas, sortColumn, sortDir]);

  const handleSort = (col) => {
    if (sortColumn === col) {
      setSortDir((d) => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(col);
      setSortDir('asc');
    }
  };

  const handleModificar = async (regla) => {
    let fuente = regla;
    const faltanDatos = !fuente?.accion || !Array.isArray(fuente?.condiciones);
    if (faltanDatos) {
      try {
        const detalle = await obtenerReglaPorId(fuente.ruleId);
        if (detalle) fuente = normalizarRegla(detalle, catalogosRef.current);
      } catch (e) {
      }
    }

    const decisionNormalizada = normalizeDecision(fuente?.accion?.decision);
    navigate('/rules', {
      state: {
        reglaParaEditar: {
          ruleId:         fuente.ruleId,
          idReglaVersion: fuente.idReglaVersion,
          version:        fuente.version,
          nombre:         fuente.nombre,
          tipoRegla:      fuente.tipoRegla,
          plan:           fuente.plan,
          idCobertura:    fuente.idCobertura ?? null,
          prioridad:      fuente.prioridad,
          overwrite:      fuente.overwrite !== undefined ? fuente.overwrite : true,
          vigente:        fuente.vigente !== undefined ? fuente.vigente : true,
          vigenteDesde:   fuente.vigenteDesde || '',
          vigenteHasta:   fuente.vigenteHasta || '',
          prerequisito:   fuente.prerequisito || false,
          condiciones:    Array.isArray(fuente.condiciones)
            ? fuente.condiciones.map(c => ({ atributo: c.atributo, operador: c.operador, valor: c.valor, tipoDato: c.tipoDato }))
            : [],
          accion: { decision: decisionNormalizada, motivo: fuente?.accion?.motivo || '' },
          ambito: 'Ambulatorio',
          tipo:   'autorizacion',
          vip:    fuente.vip || false,
        }
      }
    });
  };

  const totalPaginas = Math.ceil(totalReglas / itemsPorPagina);
  const indiceInicio = (paginaActual - 1) * itemsPorPagina;

  const irAPagina = (n) => { setPaginaActual(n); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  const generarBotonesPaginacion = () => {
    const botones = [];
    const max = 7;
    if (totalPaginas <= max) {
      for (let i = 1; i <= totalPaginas; i++) botones.push(i);
    } else if (paginaActual <= 4) {
      for (let i = 1; i <= 5; i++) botones.push(i);
      botones.push('...'); botones.push(totalPaginas);
    } else if (paginaActual >= totalPaginas - 3) {
      botones.push(1); botones.push('...');
      for (let i = totalPaginas - 4; i <= totalPaginas; i++) botones.push(i);
    } else {
      botones.push(1); botones.push('...');
      for (let i = paginaActual - 1; i <= paginaActual + 1; i++) botones.push(i);
      botones.push('...'); botones.push(totalPaginas);
    }
    return botones;
  };

  const hayFiltros = filterAccion || filterVigencia || searchTerm;
  const limpiarFiltros = () => { setFilterAccion(''); setFilterVigencia(''); setSearchTerm(''); setDebouncedSearch(''); };

  if (loading) return (
    <div className="tabla-container">
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Cargando reglas...</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="tabla-container">
      <div className="error-container">
        <p className="error-message">{error}</p>
        <button onClick={cargarInicial} className="btn btn-reload">Reintentar</button>
      </div>
    </div>
  );

  return (
    <div className="tabla-container">
      {/* Header */}
      <div className="tabla-header">
        <h2>Motor de Reglas</h2>
        <div className="header-actions">
          <div className="search-wrapper">
            <svg className="search-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M17.5 17.5L13.875 13.875M15.8333 9.16667C15.8333 12.8486 12.8486 15.8333 9.16667 15.8333C5.48477 15.8333 2.5 12.8486 2.5 9.16667C2.5 5.48477 5.48477 2.5 9.16667 2.5C12.8486 2.5 15.8333 5.48477 15.8333 9.16667Z" stroke="currentColor" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
              placeholder="Buscar por ID, nombre, acción, prioridad, plan, condición..."
            />
            {searchTerm && (
              <button type="button" className="search-clear" onClick={() => setSearchTerm('')} title="Limpiar búsqueda">×</button>
            )}
          </div>
          <button onClick={() => navigate('/rules')} className="btn btn-crear">+ Nueva Regla</button>
        </div>
      </div>

      {/* Filtros rápidos */}
      <div className="filtros-row">
        <span className="filtros-label">Acción:</span>
        {ACCIONES_FILTER.map(({ key, label, cls }) => (
          <button
            key={key}
            className={`filtro-chip badge ${cls}${filterAccion === key ? ' filtro-chip-active' : ''}`}
            onClick={() => setFilterAccion(filterAccion === key ? '' : key)}
          >
            {label}
          </button>
        ))}
        <span className="filtros-separator" />
        <span className="filtros-label">Vigencia:</span>
        {VIGENCIA_FILTER.map(({ key, label, cls }) => (
          <button
            key={key}
            className={`filtro-chip badge ${cls}${filterVigencia === key ? ' filtro-chip-active' : ''}`}
            onClick={() => setFilterVigencia(filterVigencia === key ? '' : key)}
          >
            {label}
          </button>
        ))}
        {hayFiltros && (
          <button className="filtros-limpiar" onClick={limpiarFiltros}>✕ Limpiar filtros</button>
        )}
      </div>

      {/* Meta */}
      <div className="search-meta">
        {totalReglas > 0 ? (
          <>
            Mostrando {indiceInicio + 1}–{indiceInicio + reglas.length} de {totalReglas.toLocaleString()}
            {debouncedSearch && ` · "${debouncedSearch}"`}
            {hayFiltros && ` · filtrado`}
          </>
        ) : (
          debouncedSearch || hayFiltros ? 'Sin resultados' : 'Sin resultados'
        )}
        {loadingPage && <span className="loading-page-indicator"> · cargando...</span>}
      </div>

      {/* Tabla */}
      {totalReglas === 0 && !debouncedSearch ? (
        <div className="empty-state">
          <p>No hay reglas creadas aún</p>
          <button onClick={() => navigate('/rules')} className="btn btn-crear">Crear primera regla</button>
        </div>
      ) : totalReglas === 0 && debouncedSearch ? (
        <div className="empty-state">
          <p>No hay resultados para: <strong>{debouncedSearch}</strong></p>
          <button onClick={() => setSearchTerm('')} className="btn btn-reload">Limpiar búsqueda</button>
        </div>
      ) : (
        <>
          <div className="tabla-wrapper">
            <table className={`tabla${loadingPage ? ' tabla-loading' : ''}`} style={{display:'table',tableLayout:'fixed',width:'100%'}}>
              <colgroup>
                <col style={{width:'11%'}} />
                <col style={{width:'22%'}} />
                <col style={{width:'6%'}} />
                <col style={{width:'8%'}} />
                <col style={{width:'9%'}} />
                <col style={{width:'8%'}} />
                <col style={{width:'12%'}} />
                <col style={{width:'8%'}} />
                <col style={{width:'9%'}} />
              </colgroup>
              <thead>
                <tr>
                  <th>ID Regla</th>
                  <th className="th-sortable" onClick={() => handleSort('nombre')}>
                    Nombre <SortIcon column="nombre" sortColumn={sortColumn} sortDir={sortDir} />
                  </th>
                  <th className="th-sortable text-center" onClick={() => handleSort('version')}>
                    Versión <SortIcon column="version" sortColumn={sortColumn} sortDir={sortDir} />
                  </th>
                  <th className="th-sortable text-center" onClick={() => handleSort('prioridad')}>
                    Prioridad <SortIcon column="prioridad" sortColumn={sortColumn} sortDir={sortDir} />
                  </th>
                  <th className="text-center">Jerarquía</th>
                  <th className={`vigencia-header th-sortable`} onClick={() => handleSort('vigencia')}>
                    Vigencia <SortIcon column="vigencia" sortColumn={sortColumn} sortDir={sortDir} />
                  </th>
                  <th className="th-sortable" onClick={() => handleSort('accion')}>
                    Acción <SortIcon column="accion" sortColumn={sortColumn} sortDir={sortDir} />
                  </th>
                  <th className="text-center th-sortable" onClick={() => handleSort('condiciones')}>
                    Condiciones <SortIcon column="condiciones" sortColumn={sortColumn} sortDir={sortDir} />
                  </th>
                  <th className="acciones-header">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {sortedFiltered.length === 0 ? (
                  <tr>
                    <td colSpan={9} style={{ textAlign: 'center', padding: '32px', color: '#9ca3af' }}>
                      Sin resultados con los filtros actuales
                    </td>
                  </tr>
                ) : sortedFiltered.map((regla) => {
                  const decision = normalizeDecision(regla?.accion?.decision);
                  const estadoVigencia = calcularEstadoVigencia(regla);
                  const fechaEstado = estadoVigencia.key === 'DESHABILITADA'
                    ? (regla.vigenteHasta || regla.updatedAt || regla.vigenteDesde)
                    : regla.vigenteHasta;

                  const planTexto = String(regla?.plan ?? '').trim();
                  const planKey  = planTexto.toUpperCase();
                  const tipoReglaKey = String(regla?.tipoRegla ?? '').toUpperCase();
                  const jerarquiaTexto = tipoReglaKey === 'ELEGIBILIDAD' ? 'ELEGIBILIDAD' : (planTexto || '-');

                  let planClass = 'badge-plan-default';
                  if (tipoReglaKey === 'ELEGIBILIDAD') planClass = 'badge-jerarquia-elegibilidad';
                  else if (planKey.includes('BRO250') || planKey.includes('BRONCE')) planClass = 'badge-plan-bronce';
                  else if (planKey.includes('AMN')) planClass = 'badge-plan-amn';
                  else if (planKey.includes('PRESTACION') || planKey.includes('PRESTACIÓN')) planClass = 'badge-plan-prestacion';

                  const filaInactiva = estadoVigencia.key === 'DESHABILITADA' || estadoVigencia.key === 'VENCIDA';

                  return (
                    <tr key={`${regla.ruleId}-${regla.version}`} className={filaInactiva ? 'row-deshabilitada' : ''}>
                      <td className="font-mono">
                        <span className="id-cell" title={regla.ruleId}>{regla.ruleId}</span>
                      </td>
                      <td>
                        <span className="nombre-cell" title={regla.nombre}>{regla.nombre}</span>
                      </td>
                      <td className="text-center">{regla.version}</td>
                      <td className="text-center">{regla.prioridad}</td>
                      <td className="text-center">
                        <span className={`badge ${planClass}`}>{jerarquiaTexto}</span>
                      </td>
                      <td className="text-center vigencia-cell">
                        {estadoVigencia.key === 'DESHABILITADA' ? (
                          <span className="vigencia-cruz" title="Deshabilitada" aria-label="Deshabilitada">✕</span>
                        ) : (
                          <span className={`badge badge-vigencia-${estadoVigencia.key.toLowerCase()}`}>{estadoVigencia.label}</span>
                        )}
                      </td>
                      <td>
                        <span className={`badge badge-${decision.toLowerCase()}`}>{decision}</span>
                      </td>
                      <td className="text-center">
                        <span className="condiciones-badge" title={`${regla.condiciones?.length || 0} condición(es)`}>
                          {regla.condiciones?.length || 0}
                        </span>
                      </td>
                      <td className="acciones-cell">
                        {filaInactiva ? (
                          <div className="estado-deshabilitado">
                            <span className="estado-badge">{estadoVigencia.label}</span>
                            <span className="estado-fecha">{formatearFecha(fechaEstado)}</span>
                          </div>
                        ) : (
                          <button onClick={() => handleModificar(regla)} className="btn btn-editar" title="Modificar regla">
                            Modificar
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {totalPaginas > 1 && (
            <div className="paginacion">
              <button onClick={() => irAPagina(paginaActual - 1)} disabled={paginaActual === 1 || loadingPage} className="btn-paginacion btn-paginacion-nav">← Anterior</button>
              <div className="paginacion-numeros">
                {generarBotonesPaginacion().map((pagina, index) =>
                  pagina === '...' ? (
                    <span key={`ellipsis-${index}`} className="paginacion-ellipsis">...</span>
                  ) : (
                    <button key={pagina} onClick={() => irAPagina(pagina)} disabled={loadingPage} className={`btn-paginacion ${paginaActual === pagina ? 'activo' : ''}`}>
                      {pagina}
                    </button>
                  )
                )}
              </div>
              <button onClick={() => irAPagina(paginaActual + 1)} disabled={paginaActual === totalPaginas || loadingPage} className="btn-paginacion btn-paginacion-nav">Siguiente →</button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default VistaReglas;
